/**
 * R3-A OPTION B (founder decision 2026-10-21, requirement 4): "Reconcile immediately whenever Dayboard opens or
 * reconnects, avoiding presentation of stale scheduling data as current."
 *
 * The server keeps Run Long AutoBump going with every browser closed (pg_cron → run-long-autobump). A board that
 * opens, comes back from a hidden tab, comes back online, or recovers its realtime channel asks the server to
 * reconcile NOW (force: skip the 10 s freshness window, never the lease) and, while that first / recovery reconcile is
 * pending, says so ("Updating schedule…") instead of presenting what it loaded as live. When the answer arrives it
 * dispatches the existing `ticket-updated` invalidation so the board re-reads the reconciled times.
 *
 * Staleness: the members' status read (run_long_autobump_status) is polled every minute; if the shop has live work
 * today (`due`) and its last completed reconcile is older than 3 minutes the board says "Schedule last reconciled h:mm", never "live".
 *
 * oct20c (PR #13 audit, finding 1): a reconcile that did not FULLY succeed (PARTIAL / FAILED — some lanes, the lane
 * walk, the alert evaluation or claimed chain growth did not finish) is `failing`: the board says "Schedule could not
 * be fully updated — retrying" (the server retries every minute), never fresh / live. `lastReconciledAt` is the last
 * FULLY successful reconcile.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { clock } from '@/utils/clock';
import { readRunLongReconcileStatus, requestRunLongReconcile } from '@/lib/scheduling/runLongReconcileClient';

export const RECONCILE_STALE_AFTER_MS = 3 * 60_000;
const STATUS_POLL_MS = 60_000;

export interface RunLongReconcileView {
  /** A first-load / recovery reconcile is in flight. */
  pending: boolean;
  /** When the server last completed a reconcile of this shop. */
  lastReconciledAt: Date | null;
  /** The last completed reconcile is older than 3 minutes (or the server never answered). */
  stale: boolean;
  /** oct20c: the server's last reconcile of this shop did not fully succeed (it is retrying). */
  failing?: boolean;
}

/**
 * Pure: is a reconcile that completed at `last` stale at `nowMs`? Unknown is not stale until a read answered, and a
 * shop with no live work today (`due` false — the cron has nothing to reconcile there) is never stale.
 */
export function isReconcileStale(last: Date | null, nowMs: number, answered: boolean, due = true): boolean {
  if (!answered || !due) return false;
  if (!last) return true;
  return nowMs - last.getTime() > RECONCILE_STALE_AFTER_MS;
}

/** Pure: does the server's last reconcile leave this board NOT fully reconciled? (Only a shop with live work counts.) */
export function isReconcileFailing(lastOutcome: 'FULL_SUCCESS' | 'PARTIAL' | 'FAILED' | null | undefined, due = true): boolean {
  return due && (lastOutcome === 'PARTIAL' || lastOutcome === 'FAILED');
}

export function useRunLongReconcileOnOpen(shopId: string | null | undefined): RunLongReconcileView & { reconcileNow: () => void } {
  const [pending, setPending] = useState(false);
  const [lastReconciledAt, setLastReconciledAt] = useState<Date | null>(null);
  const [answered, setAnswered] = useState(false);
  const [due, setDue] = useState(true);
  const [failing, setFailing] = useState(false);
  const [nowMs, setNowMs] = useState(() => clock.nowMs());
  const shopRef = useRef<string | null>(shopId ?? null);
  shopRef.current = shopId ?? null;

  const reconcileNow = useCallback(() => {
    const shop = shopRef.current;
    if (!shop) return;
    setPending(true);
    void requestRunLongReconcile(shop, { force: true }).then((r) => {
      if (shopRef.current !== shop) return;
      setPending(false);
      setNowMs(clock.nowMs());
      if (r.outcome !== 'ERROR') {
        setAnswered(true);
        if (r.reconciledAt) setLastReconciledAt(new Date(r.reconciledAt));
        // a partial / failed run is never presented as fresh; a full success (or a fresh one) clears it
        if (r.outcome === 'PARTIAL' || r.outcome === 'FAILED') setFailing(true);
        else if (r.outcome !== 'BUSY_TIMEOUT') setFailing(false);
      }
      // The board re-reads what the server reconciled (the existing fallback refetch).
      try { window.dispatchEvent(new CustomEvent('ticket-updated')); } catch { /* best-effort */ }
    });
  }, []);

  // Open (and shop change): reconcile now.
  useEffect(() => {
    setLastReconciledAt(null);
    setAnswered(false);
    setFailing(false);
    if (!shopId) return;
    reconcileNow();
  }, [shopId, reconcileNow]);

  // Visibility return and coming back online.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onVisible = () => {
      if (typeof document !== 'undefined' && !document.hidden) reconcileNow();
    };
    const onOnline = () => reconcileNow();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [reconcileNow]);

  // Staleness: the shop's last completed reconcile, every minute.
  useEffect(() => {
    if (!shopId) return;
    let cancelled = false;
    const poll = async () => {
      const s = await readRunLongReconcileStatus(shopId);
      if (cancelled) return;
      setNowMs(clock.nowMs());
      if (s) {
        setAnswered(true);
        setDue(s.due);
        setLastReconciledAt(s.lastCompletedAt ? new Date(s.lastCompletedAt) : null);
        if (!s.inProgress) setFailing(isReconcileFailing(s.lastOutcome, s.due));
      }
    };
    const handle = setInterval(() => { void poll(); }, STATUS_POLL_MS);
    return () => { cancelled = true; clearInterval(handle); };
  }, [shopId]);

  return { pending, lastReconciledAt, stale: isReconcileStale(lastReconciledAt, nowMs, answered, due), failing, reconcileNow };
}
